// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HomeworkTask, HomeworkTaskType } from '../../types';
import { MathMixedText } from './MathMixedText';
import {
  getMcqOptionLabel,
  isMcqTfStudentAnswerCorrect,
  movementTaskHasDefinedCorrectAnswer,
  shouldShowMcqAbcdLabels,
} from '../../utils/homeworkMcq';
import { formatAssetUrl } from '../../services/apiService';
import { playAnswerTete, playAnswerTingTing } from '../../utils/movementFeedbackAudio';
import {
  createMovementEngine,
  detectFrame,
  DIRECTION_LABELS,
  type EngineInstance,
  type MovementTrackMode,
} from '../../utils/movementPoseEngine';
import { OptionPoseInline, getPoseHintForSlot, splitLabelsForCamera } from './MovementHintIllustrations';
import { CameraPoseGuides } from './CameraPoseGuides';
import { MovementAnswerFeedback } from './MovementAnswerFeedback';
import { Cog6ToothIcon, EyeIcon, EyeSlashIcon, PauseIcon, PlayIcon, XMarkIcon } from './Icons';

/** Nhạc nền minh họa (instrumental, có thể thay bằng file trong /public/audio/) */
const MOVEMENT_BG_MUSIC_URL =
  'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';

const LS_AUDIO_VOL = 'movementModeBgVolume';
const LS_CAMERA_FACING = 'movementModeCameraFacing';
const LS_HEAD_SENS = 'movementModeHeadSensitivity';
const LS_UI_FONT_PX = 'movementModeUiFontPx';
const LS_UI_TEXT_COLOR = 'movementModeUiTextColor';
const LS_QUESTION_FONT_PX = 'movementModeQuestionFontPx';
const LS_HOLD_CONFIRM_MS = 'movementModeHoldConfirmMs';
const LS_SHOW_CAMERA_PANEL = 'movementModeShowCameraPanel';
const LS_SHOW_CAMERA_PREVIEW = 'movementModeShowCameraPreview';

function movementPrefsStorageKey(homeworkId: string) {
  return `movementModeHwPrefs_${homeworkId}`;
}

type CameraFacing = 'user' | 'environment';

/** Giữ cùng hướng (lệch so với trung tâm khuôn mặt) trong khoảng này thì chốt đáp án — có thể chỉnh trong Cài đặt */
const HOLD_MIN_MS = 1500;
const HOLD_MAX_MS = 8000;
const HOLD_DEFAULT_MS = 3000;
/** Sau khoảng này mới sáng gợi ý trên ô camera */
const GUIDE_SHOW_MS = 500;
const COOLDOWN_MS = 750;
const AUTO_NEXT_MS = 1150;

function getMovementScorePresentation(
  points10: number | null,
  graded: number
): { title: string; subtitle: string; panel: string; accent: string } {
  if (graded === 0 || points10 === null) {
    return {
      title: 'Hoàn thành phần vận động',
      subtitle: 'Bạn đã trả lời hết các câu trắc nghiệm / đúng sai trong chế độ này.',
      panel: 'border-white/20 bg-slate-900/95',
      accent: 'text-teal-200',
    };
  }
  if (points10 >= 9) {
    return {
      title: 'Xuất sắc!',
      subtitle: 'Kết quả rất tốt — cứ duy trì nhé!',
      panel: 'border-amber-400/50 bg-gradient-to-br from-amber-950/90 to-teal-950/90',
      accent: 'text-amber-200',
    };
  }
  if (points10 >= 8) {
    return {
      title: 'Rất tốt!',
      subtitle: 'Bạn nắm vững phần lớn nội dung.',
      panel: 'border-emerald-400/40 bg-emerald-950/85',
      accent: 'text-emerald-200',
    };
  }
  if (points10 >= 7) {
    return {
      title: 'Khá tốt!',
      subtitle: 'Còn một chút là đạt mức cao hơn — cố gắng thêm chút nữa nhé.',
      panel: 'border-teal-400/35 bg-teal-950/80',
      accent: 'text-teal-200',
    };
  }
  if (points10 >= 5) {
    return {
      title: 'Đạt yêu cầu',
      subtitle: 'Ôn lại phần chưa chắc để tiến bộ hơn nhé.',
      panel: 'border-sky-400/30 bg-slate-900/95',
      accent: 'text-sky-200',
    };
  }
  return {
    title: 'Cần cố gắng thêm',
    subtitle: 'Đừng ngại ôn lại bài — mỗi lần làm là một lần tiến bộ.',
    panel: 'border-rose-400/35 bg-rose-950/80',
    accent: 'text-rose-100',
  };
}

type Props = {
  open: boolean;
  onClose: () => void;
  /** Lưu cài đặt hiển thị theo bài tập (localStorage) */
  homeworkId?: string;
  shuffledTasks: HomeworkTask[];
  shuffledOptions: Record<string, string[]>;
  answers: Record<string, any>;
  onAnswerChange: (taskId: string, value: any) => void;
  isSubmitted: boolean;
};

function getMovementTaskList(tasks: HomeworkTask[]): HomeworkTask[] {
  return tasks.filter(
    (t) =>
      t.taskType === HomeworkTaskType.MultipleChoiceSingle ||
      t.taskType === HomeworkTaskType.MultipleChoiceMulti ||
      t.taskType === HomeworkTaskType.TrueFalse
  );
}

function isTaskAnswered(task: HomeworkTask, ans: Record<string, any>): boolean {
  const a = ans[task.id];
  if (task.taskType === HomeworkTaskType.MultipleChoiceMulti) return Array.isArray(a) && a.length > 0;
  return a !== undefined && a !== null && a !== '';
}

function nextUnansweredIndex(
  movementTasks: HomeworkTask[],
  afterIdx: number,
  ans: Record<string, any>
): number | null {
  const n = movementTasks.length;
  for (let i = afterIdx + 1; i < n; i++) {
    if (!isTaskAnswered(movementTasks[i], ans)) return i;
  }
  for (let i = 0; i <= afterIdx; i++) {
    if (!isTaskAnswered(movementTasks[i], ans)) return i;
  }
  return null;
}

export const HomeworkMovementMode: React.FC<Props> = ({
  open,
  onClose,
  homeworkId,
  shuffledTasks,
  shuffledOptions,
  answers,
  onAnswerChange,
  isSubmitted,
}) => {
  const movementTasks = getMovementTaskList(shuffledTasks);
  const [idx, setIdx] = useState(0);
  const [trackMode, setTrackMode] = useState<MovementTrackMode>('head');
  const [engine, setEngine] = useState<EngineInstance | null>(null);
  const [engineError, setEngineError] = useState<string | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [guideSlot, setGuideSlot] = useState<number | null>(null);
  const [audioVol, setAudioVol] = useState(0.28);
  const [allAnsweredBanner, setAllAnsweredBanner] = useState(false);
  const [answerFeedback, setAnswerFeedback] = useState<'correct' | 'wrong' | null>(null);
  /** Chỉ phát nhạc nền sau khi phát xong âm thanh câu hỏi (hoặc không có file) */
  const [questionSpeechDone, setQuestionSpeechDone] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /** Màn kiểm tra camera + cử chỉ trước khi làm bài */
  const [cameraSetupOpen, setCameraSetupOpen] = useState(false);
  /** Đồng bộ nút phát/tạm dừng âm thanh câu hỏi */
  const [questionAudioPlaying, setQuestionAudioPlaying] = useState(false);
  const [liveTelemetry, setLiveTelemetry] = useState<{
    signalLabel: string | null;
    signalDesc: string | null;
    targetSlot: number | null;
    holdRemainSec: number | null;
    guideVisible: boolean;
    cooldownRemainSec: number | null;
  }>({
    signalLabel: null,
    signalDesc: null,
    targetSlot: null,
    holdRemainSec: null,
    guideVisible: false,
    cooldownRemainSec: null,
  });
  /** null = không đếm; số hoặc 'go' = overlay trên camera */
  const [countdownTick, setCountdownTick] = useState<number | 'go' | null>(null);
  const [detectionActive, setDetectionActive] = useState(false);
  const [cameraFacing, setCameraFacing] = useState<CameraFacing>(() => {
    try {
      const v = localStorage.getItem(LS_CAMERA_FACING);
      if (v === 'environment' || v === 'user') return v;
    } catch {
      /* noop */
    }
    return 'user';
  });
  /** Độ nhạy nghiêng đầu (giáo viên): càng lớn càng dễ nhận */
  const [headSensitivity, setHeadSensitivity] = useState(() => {
    try {
      const v = parseFloat(localStorage.getItem(LS_HEAD_SENS) || '');
      if (Number.isFinite(v)) return Math.min(2.75, Math.max(0.35, v));
    } catch {
      /* noop */
    }
    return 1;
  });
  const [uiFontPx, setUiFontPx] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(LS_UI_FONT_PX) || '', 10);
      if (Number.isFinite(v)) return Math.min(22, Math.max(11, v));
    } catch {
      /* noop */
    }
    return 13;
  });
  const [uiTextColor, setUiTextColor] = useState(() => {
    try {
      const v = localStorage.getItem(LS_UI_TEXT_COLOR);
      if (v && /^#[0-9A-Fa-f]{6}$/.test(v)) return v;
    } catch {
      /* noop */
    }
    return '#e2e8f0';
  });
  const [questionFontPx, setQuestionFontPx] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(LS_QUESTION_FONT_PX) || '', 10);
      if (Number.isFinite(v)) return Math.min(22, Math.max(12, v));
    } catch {
      /* noop */
    }
    return 15;
  });
  const [holdConfirmMs, setHoldConfirmMs] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(LS_HOLD_CONFIRM_MS) || '', 10);
      if (Number.isFinite(v)) return Math.min(HOLD_MAX_MS, Math.max(HOLD_MIN_MS, v));
    } catch {
      /* noop */
    }
    return HOLD_DEFAULT_MS;
  });
  const [showCameraDataPanel, setShowCameraDataPanel] = useState(() => {
    try {
      const v = localStorage.getItem(LS_SHOW_CAMERA_PANEL);
      if (v === '0') return false;
    } catch {
      /* noop */
    }
    return true;
  });
  const [cameraDataDetailOpen, setCameraDataDetailOpen] = useState(false);
  /** Hiển thị khung video trên màn hình (tắt vẫn giữ stream off-screen để nhận diện chạy) */
  const [showCameraPreview, setShowCameraPreview] = useState(() => {
    try {
      const v = localStorage.getItem(LS_SHOW_CAMERA_PREVIEW);
      if (v === '0') return false;
    } catch {
      /* noop */
    }
    return true;
  });
  /** Chỉ sau khi nhấn «Bắt đầu» mới đếm ngược và bật nhận diện */
  const [sessionStarted, setSessionStarted] = useState(false);
  /** Modal tổng điểm khi làm xong — gập sau khi học sinh bấm Đã hiểu */
  const [movementScoreDismissed, setMovementScoreDismissed] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoSetupRef = useRef<HTMLVideoElement | null>(null);
  const cameraSetupOpenRef = useRef(false);
  const detectionRunningForLoopRef = useRef(false);
  const headSensitivityRef = useRef(1);
  const holdConfirmMsRef = useRef(HOLD_DEFAULT_MS);
  const countdownStartedRef = useRef(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const questionAudioRef = useRef<HTMLAudioElement | null>(null);
  const telemetryEmitAtRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);
  /** Cùng một slot đang được giữ để đo thời gian */
  const holdSlotRef = useRef<number | null>(null);
  const holdStartedAtRef = useRef<number | null>(null);
  const lastHintAtRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const engineRef = useRef<EngineInstance | null>(null);
  const answersRef = useRef(answers);
  const guideSlotSyncRef = useRef<number | null>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const feedbackClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevAllAnsweredRef = useRef(false);

  answersRef.current = answers;
  headSensitivityRef.current = headSensitivity;
  holdConfirmMsRef.current = holdConfirmMs;

  const triggerAnswerFeedback = useCallback((t: HomeworkTask, answer: any) => {
    if (!movementTaskHasDefinedCorrectAnswer(t)) return;
    const ok = isMcqTfStudentAnswerCorrect(t, answer);
    if (feedbackClearRef.current) clearTimeout(feedbackClearRef.current);
    setAnswerFeedback(ok ? 'correct' : 'wrong');
    if (ok) playAnswerTingTing();
    else playAnswerTete();
    feedbackClearRef.current = setTimeout(() => setAnswerFeedback(null), 1450);
  }, []);

  const toggleQuestionAudio = useCallback(() => {
    const el = questionAudioRef.current;
    if (!el?.src) return;
    if (el.paused) void el.play().catch(() => {});
    else el.pause();
  }, []);

  const task = movementTasks[idx];
  const isTF = task?.taskType === HomeworkTaskType.TrueFalse;
  const isMulti = task?.taskType === HomeworkTaskType.MultipleChoiceMulti;
  const options: string[] = isTF ? ['Đúng', 'Sai'] : shuffledOptions[task?.id || ''] || task?.content?.options || [];
  const optionCount = options.length;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const shuffledOptionsRef = useRef(shuffledOptions);
  shuffledOptionsRef.current = shuffledOptions;
  /** Tránh effect phụ thuộc `answers` (object mới mỗi render từ parent) gây lặp cập nhật */
  const movementAnswerFingerprint = movementTasks
    .map((t) => {
      const a = answers[t.id];
      if (Array.isArray(a)) return a.join('\u0002');
      return a === undefined || a === null ? '' : String(a);
    })
    .join('\u0001');
  const movementScoreStats = useMemo(() => {
    let correct = 0;
    let graded = 0;
    for (const t of movementTasks) {
      if (!movementTaskHasDefinedCorrectAnswer(t)) continue;
      graded += 1;
      if (isMcqTfStudentAnswerCorrect(t, answers[t.id])) correct += 1;
    }
    const points10 =
      graded > 0 ? Math.round((correct / graded) * 100) / 10 : null;
    return { correct, graded, points10 };
  }, [movementTasks, movementAnswerFingerprint, answers]);
  const showMcqLetters = shouldShowMcqAbcdLabels(optionCount, !!isTF);

  const answeredCount = movementTasks.filter((t) => isTaskAnswered(t, answers)).length;
  const allAnswered =
    movementTasks.length > 0 && movementTasks.every((t) => isTaskAnswered(t, answers));
  const detectionRunning = detectionActive && !allAnswered;
  /** Trong màn thiết lập: luôn chạy nhận diện thử (không phụ thuộc đếm ngược / đã trả lời hết) */
  const detectionRunningForLoopUi =
    cameraSetupOpen ? open && !isSubmitted && !camError : detectionRunning;

  useEffect(() => {
    cameraSetupOpenRef.current = cameraSetupOpen;
  }, [cameraSetupOpen]);

  useEffect(() => {
    detectionRunningForLoopRef.current = cameraSetupOpen
      ? Boolean(open && !isSubmitted && !camError)
      : detectionRunning;
  }, [cameraSetupOpen, open, isSubmitted, camError, detectionRunning]);

  useEffect(() => {
    try {
      const v = localStorage.getItem(LS_AUDIO_VOL);
      if (v !== null) {
        const n = parseFloat(v);
        if (Number.isFinite(n)) setAudioVol(Math.min(1, Math.max(0, n)));
      }
    } catch {
      /* noop */
    }
  }, [open]);

  useEffect(() => {
    if (!open || !homeworkId) return;
    try {
      const raw = localStorage.getItem(movementPrefsStorageKey(homeworkId));
      if (!raw) return;
      const p = JSON.parse(raw);
      if (typeof p.uiFontPx === 'number') setUiFontPx(Math.min(22, Math.max(11, p.uiFontPx)));
      if (typeof p.questionFontPx === 'number') setQuestionFontPx(Math.min(22, Math.max(12, p.questionFontPx)));
      if (typeof p.uiTextColor === 'string' && /^#[0-9A-Fa-f]{6}$/.test(p.uiTextColor)) setUiTextColor(p.uiTextColor);
      if (typeof p.headSensitivity === 'number') setHeadSensitivity(Math.min(2.75, Math.max(0.35, p.headSensitivity)));
      if (typeof p.audioVol === 'number') setAudioVol(Math.min(1, Math.max(0, p.audioVol)));
      if (p.cameraFacing === 'user' || p.cameraFacing === 'environment') setCameraFacing(p.cameraFacing);
      if (typeof p.holdConfirmMs === 'number') {
        setHoldConfirmMs(Math.min(HOLD_MAX_MS, Math.max(HOLD_MIN_MS, Math.round(p.holdConfirmMs))));
      }
      if (typeof p.showCameraDataPanel === 'boolean') setShowCameraDataPanel(p.showCameraDataPanel);
      if (typeof p.showCameraPreview === 'boolean') setShowCameraPreview(p.showCameraPreview);
    } catch {
      /* noop */
    }
  }, [open, homeworkId]);

  useEffect(() => {
    if (!open || !homeworkId) return;
    try {
      localStorage.setItem(
        movementPrefsStorageKey(homeworkId),
        JSON.stringify({
          uiFontPx,
          questionFontPx,
          uiTextColor,
          headSensitivity,
          audioVol,
          cameraFacing,
          holdConfirmMs,
          showCameraDataPanel,
          showCameraPreview,
        })
      );
    } catch {
      /* noop */
    }
  }, [
    open,
    homeworkId,
    uiFontPx,
    questionFontPx,
    uiTextColor,
    headSensitivity,
    audioVol,
    cameraFacing,
    holdConfirmMs,
    showCameraDataPanel,
    showCameraPreview,
  ]);

  useEffect(() => {
    if (!open || homeworkId) return;
    try {
      localStorage.setItem(LS_HOLD_CONFIRM_MS, String(holdConfirmMs));
    } catch {
      /* noop */
    }
  }, [open, homeworkId, holdConfirmMs]);

  useEffect(() => {
    if (!open || homeworkId) return;
    try {
      localStorage.setItem(LS_SHOW_CAMERA_PANEL, showCameraDataPanel ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [open, homeworkId, showCameraDataPanel]);

  useEffect(() => {
    if (!open || homeworkId) return;
    try {
      localStorage.setItem(LS_SHOW_CAMERA_PREVIEW, showCameraPreview ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [open, homeworkId, showCameraPreview]);

  useEffect(() => {
    if (!open || homeworkId) return;
    try {
      localStorage.setItem(LS_QUESTION_FONT_PX, String(questionFontPx));
    } catch {
      /* noop */
    }
  }, [open, homeworkId, questionFontPx]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_AUDIO_VOL, String(audioVol));
    } catch {
      /* noop */
    }
    if (audioRef.current) {
      audioRef.current.volume = audioVol;
      if (audioVol < 0.02) {
        audioRef.current.pause();
      }
    }
  }, [audioVol]);

  useEffect(() => {
    if (!open || isSubmitted) {
      const qa = questionAudioRef.current;
      if (qa) {
        qa.pause();
        qa.removeAttribute('src');
        qa.load();
      }
      setQuestionSpeechDone(true);
      setQuestionAudioPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      return;
    }
    const rawUrl = task?.audioUrl ? String(task.audioUrl).trim() : '';
    const url = rawUrl ? formatAssetUrl(rawUrl) : '';
    if (!url) {
      const qa = questionAudioRef.current;
      if (qa) {
        qa.pause();
        qa.removeAttribute('src');
        qa.load();
      }
      setQuestionSpeechDone(true);
      setQuestionAudioPlaying(false);
      return;
    }

    setQuestionSpeechDone(false);
    const el = questionAudioRef.current;
    if (!el) {
      setQuestionSpeechDone(true);
      return;
    }

    let cancelled = false;
    const onEnd = () => {
      if (!cancelled) setQuestionSpeechDone(true);
    };
    const onErr = () => {
      if (!cancelled) setQuestionSpeechDone(true);
    };

    el.pause();
    el.src = url;
    el.load();
    el.addEventListener('ended', onEnd);
    el.addEventListener('error', onErr);
    el.play().catch(() => {
      /* autoplay bị chặn — học sinh bấm Phát; nhạc nền chờ đến khi phát xong */
    });

    return () => {
      cancelled = true;
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('error', onErr);
      el.pause();
    };
  }, [open, isSubmitted, task?.id, task?.audioUrl]);

  useEffect(() => {
    const el = questionAudioRef.current;
    if (!el) return;
    const onPlay = () => setQuestionAudioPlaying(true);
    const onPause = () => setQuestionAudioPlaying(false);
    el.addEventListener('playing', onPlay);
    el.addEventListener('pause', onPause);
    return () => {
      el.removeEventListener('playing', onPlay);
      el.removeEventListener('pause', onPause);
    };
  }, [task?.id, task?.audioUrl]);

  useEffect(() => {
    if (!open || isSubmitted) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      return;
    }
    const a = audioRef.current;
    if (!a) return;
    a.volume = audioVol;
    if (audioVol < 0.02 || !questionSpeechDone) {
      a.pause();
      return;
    }
    a.loop = true;
    a.play().catch(() => {});
  }, [open, isSubmitted, audioVol, questionSpeechDone]);

  const scheduleAutoNext = useCallback(
    (mergedAnswers: Record<string, any>, fromIdx: number) => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        const next = nextUnansweredIndex(movementTasks, fromIdx, mergedAnswers);
        if (next === null) {
          setAllAnsweredBanner(true);
          return;
        }
        setIdx(next);
        setAllAnsweredBanner(false);
      }, AUTO_NEXT_MS);
    },
    [movementTasks]
  );

  const applySlot = useCallback(
    (slot: number) => {
      const opts = optionsRef.current;
      if (!task || isSubmitted || slot < 0 || slot >= opts.length) return;
      const val = opts[slot];
      const tid = task.id;
      let merged = answersRef.current;

      if (isTF || task.taskType === HomeworkTaskType.MultipleChoiceSingle) {
        onAnswerChange(tid, val);
        merged = { ...answersRef.current, [tid]: val };
        triggerAnswerFeedback(task, val);
        scheduleAutoNext(merged, idx);
      } else if (isMulti) {
        const cur = Array.isArray(answersRef.current[tid]) ? answersRef.current[tid] : [];
        const next = cur.includes(val) ? cur.filter((o: string) => o !== val) : [...cur, val];
        onAnswerChange(tid, next);
      }
    },
    [task, isSubmitted, isTF, isMulti, onAnswerChange, scheduleAutoNext, idx, triggerAnswerFeedback]
  );

  const applySlotRef = useRef(applySlot);
  applySlotRef.current = applySlot;

  useEffect(() => {
    engineRef.current = engine;
  }, [engine]);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
    setEngineError(null);
    setCamError(null);
    setHint(null);
    setGuideSlot(null);
    guideSlotSyncRef.current = null;
    setAllAnsweredBanner(false);
    setAnswerFeedback(null);
    if (advanceTimerRef.current) {
      clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (feedbackClearRef.current) {
      clearTimeout(feedbackClearRef.current);
      feedbackClearRef.current = null;
    }
    countdownStartedRef.current = false;
    setCountdownTick(null);
    setDetectionActive(false);
    prevAllAnsweredRef.current = false;
    setSettingsOpen(false);
    setCameraSetupOpen(false);
    setCameraDataDetailOpen(false);
    setSessionStarted(false);
    setMovementScoreDismissed(false);
  }, [open]);

  useEffect(() => {
    if (!allAnswered) {
      prevAllAnsweredRef.current = false;
      return;
    }
    if (!prevAllAnsweredRef.current) {
      prevAllAnsweredRef.current = true;
      setDetectionActive(false);
    }
  }, [allAnswered]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_CAMERA_FACING, cameraFacing);
    } catch {
      /* noop */
    }
  }, [cameraFacing]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_HEAD_SENS, String(headSensitivity));
    } catch {
      /* noop */
    }
  }, [headSensitivity]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_UI_FONT_PX, String(uiFontPx));
    } catch {
      /* noop */
    }
  }, [uiFontPx]);

  useEffect(() => {
    try {
      localStorage.setItem(LS_UI_TEXT_COLOR, uiTextColor);
    } catch {
      /* noop */
    }
  }, [uiTextColor]);

  useEffect(() => {
    if (camError && sessionStarted) {
      countdownStartedRef.current = true;
      setCountdownTick(null);
      setDetectionActive(true);
    }
  }, [camError, sessionStarted]);

  useEffect(() => {
    if (!open || isSubmitted || camError || !sessionStarted) return;
    countdownStartedRef.current = false;
    setCountdownTick(null);
    setDetectionActive(false);
    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let rafId = 0;

    const startCountdown = () => {
      if (cancelled || countdownStartedRef.current) return;
      countdownStartedRef.current = true;
      setDetectionActive(false);
      setCountdownTick(3);
      timers.push(window.setTimeout(() => { if (!cancelled) setCountdownTick(2); }, 850));
      timers.push(window.setTimeout(() => { if (!cancelled) setCountdownTick(1); }, 1700));
      timers.push(window.setTimeout(() => { if (!cancelled) setCountdownTick('go'); }, 2550));
      timers.push(
        window.setTimeout(() => {
          if (!cancelled) {
            setCountdownTick(null);
            setDetectionActive(true);
          }
        }, 3200)
      );
    };

    const waitVideo = () => {
      if (cancelled) return;
      if (videoRef.current?.videoWidth) {
        startCountdown();
        return;
      }
      rafId = requestAnimationFrame(waitVideo);
    };
    waitVideo();

    timers.push(
      window.setTimeout(() => {
        if (!cancelled && !countdownStartedRef.current) {
          countdownStartedRef.current = true;
          setCountdownTick(null);
          setDetectionActive(true);
        }
      }, 12000)
    );

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      timers.forEach(clearTimeout);
    };
  }, [open, camError, isSubmitted, cameraFacing, sessionStarted]);

  useEffect(() => {
    if (!open || isSubmitted) return;
    let cancelled = false;
    (async () => {
      try {
        setEngineError(null);
        const eng = await createMovementEngine(trackMode);
        if (cancelled) {
          eng.close();
          return;
        }
        setEngine(eng);
      } catch (e: any) {
        setEngine(null);
        setEngineError(e?.message || 'Không tải được mô hình nhận diện.');
      }
    })();
    return () => {
      cancelled = true;
      setEngine((prev) => {
        prev?.close();
        return null;
      });
    };
  }, [open, trackMode, isSubmitted]);

  useEffect(() => {
    if (!open || isSubmitted) return;
    let cancelled = false;
    (async () => {
      try {
        setCamError(null);
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: cameraFacing },
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        if (videoSetupRef.current) {
          videoSetupRef.current.srcObject = stream;
          await videoSetupRef.current.play().catch(() => {});
        }
      } catch {
        setCamError(
          cameraFacing === 'environment'
            ? 'Không mở được camera sau. Thử chọn «Trước», cấp quyền trình duyệt, hoặc chọn đáp án bằng nút bên dưới.'
            : 'Không mở được camera. Hãy cấp quyền hoặc chọn đáp án bằng nút bên dưới.'
        );
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open, isSubmitted, cameraFacing]);

  useEffect(() => {
    if (!open || !streamRef.current) return;
    const v = videoSetupRef.current;
    if (!v) return;
    v.srcObject = streamRef.current;
    void v.play().catch(() => {});
  }, [open, cameraFacing, cameraSetupOpen]);

  /** RAF: applySlot/options/shuffledOptions đọc qua ref để dependency không đổi mỗi render (tránh «Maximum update depth exceeded»). */
  useEffect(() => {
    if (!open || !engine || isSubmitted) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const loop = () => {
      const video = cameraSetupOpenRef.current ? videoSetupRef.current : videoRef.current;
      const eng = engineRef.current;
      if (!eng || !video || !video.videoWidth) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      const now = performance.now();
      if (now < cooldownUntilRef.current) {
        const remCd = Math.max(0, (cooldownUntilRef.current - now) / 1000);
        const tick = performance.now();
        if (tick - telemetryEmitAtRef.current > 100) {
          telemetryEmitAtRef.current = tick;
          setLiveTelemetry((prev) => ({ ...prev, cooldownRemainSec: remCd }));
        }
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      if (!detectionRunningForLoopRef.current) {
        const t = performance.now();
        if (t - telemetryEmitAtRef.current > 200) {
          telemetryEmitAtRef.current = t;
          setLiveTelemetry({
            signalLabel: null,
            signalDesc: null,
            targetSlot: null,
            holdRemainSec: null,
            guideVisible: false,
            cooldownRemainSec: null,
          });
        }
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const t = movementTasks[idx];
      if (!t) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }
      const tf = t.taskType === HomeworkTaskType.TrueFalse;
      const oc = tf ? 2 : shuffledOptionsRef.current[t.id]?.length || t.content?.options?.length || 0;
      const { slotIndex, rawLabel } = detectFrame(eng, video, performance.now(), oc, tf, {
        headMirrorSelfieLR: cameraFacing === 'user',
        headRelaxedLR: true,
        headSensitivity: headSensitivityRef.current,
      });

      const describeDetection = () => {
        if (!rawLabel) return '';
        if (eng.mode === 'head' && rawLabel in DIRECTION_LABELS) {
          return DIRECTION_LABELS[rawLabel];
        }
        if (rawLabel === 'left_arm') return 'giang tay trái';
        if (rawLabel === 'right_arm') return 'giang tay phải';
        return String(rawLabel);
      };

      const emitTelemetry = (payload: {
        signalLabel: string | null;
        signalDesc: string | null;
        targetSlot: number | null;
        holdRemainSec: number | null;
        guideVisible: boolean;
        cooldownRemainSec: null;
      }) => {
        const t = performance.now();
        if (t - telemetryEmitAtRef.current < 90) return;
        telemetryEmitAtRef.current = t;
        setLiveTelemetry(payload);
      };

      if (slotIndex === null) {
        holdSlotRef.current = null;
        holdStartedAtRef.current = null;
        if (guideSlotSyncRef.current !== null) {
          guideSlotSyncRef.current = null;
          setGuideSlot(null);
        }
        const holdSec = (holdConfirmMsRef.current / 1000).toFixed(1);
        setHint(
          rawLabel
            ? cameraSetupOpenRef.current
              ? `[Thiết lập] Đang thấy: ${describeDetection()} — thử giữ ~${holdSec}s`
              : `Đang thấy: ${describeDetection()} — giữ nghiêng đầu ~${holdSec}s để chọn`
            : null
        );
        emitTelemetry({
          signalLabel: rawLabel ? String(rawLabel) : null,
          signalDesc: rawLabel ? describeDetection() : null,
          targetSlot: null,
          holdRemainSec: null,
          guideVisible: false,
          cooldownRemainSec: null,
        });
      } else {
        if (holdSlotRef.current !== slotIndex) {
          holdSlotRef.current = slotIndex;
          holdStartedAtRef.current = now;
          lastHintAtRef.current = 0;
        }
        const t0 = holdStartedAtRef.current;
        const held = t0 != null ? now - t0 : 0;
        const holdNeed = holdConfirmMsRef.current;
        const rem = Math.max(0, (holdNeed - held) / 1000);
        const guideOk = held >= GUIDE_SHOW_MS;
        const g = guideOk ? slotIndex : null;
        if (g !== guideSlotSyncRef.current) {
          guideSlotSyncRef.current = g;
          setGuideSlot(g);
        }
        if (held >= holdNeed) {
          if (cameraSetupOpenRef.current) {
            holdSlotRef.current = null;
            holdStartedAtRef.current = null;
            cooldownUntilRef.current = now + COOLDOWN_MS;
            setHint(
              `[Thiết lập] Nhận diện ổn định — có thể bấm «Hoàn tất thiết lập» để làm bài.`
            );
          } else {
            applySlotRef.current(slotIndex);
            holdSlotRef.current = null;
            holdStartedAtRef.current = null;
            cooldownUntilRef.current = now + COOLDOWN_MS;
            setHint(`Đã chọn: ${optionsRef.current[slotIndex]?.slice(0, 48)}…`);
          }
        } else if (now - lastHintAtRef.current > 120) {
          lastHintAtRef.current = now;
          setHint(
            cameraSetupOpenRef.current
              ? `[Thiết lập] Giữ ${describeDetection()} — còn khoảng ${rem.toFixed(1)}s…`
              : `Giữ ${describeDetection()} — còn khoảng ${rem.toFixed(1)}s…`
          );
        }
        emitTelemetry({
          signalLabel: rawLabel ? String(rawLabel) : null,
          signalDesc: describeDetection(),
          targetSlot: slotIndex,
          holdRemainSec: rem,
          guideVisible: guideOk,
          cooldownRemainSec: null,
        });
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    open,
    engine,
    idx,
    movementTasks,
    isSubmitted,
    detectionRunning,
    cameraFacing,
    cameraSetupOpen,
  ]);

  useEffect(() => {
    holdSlotRef.current = null;
    holdStartedAtRef.current = null;
    lastHintAtRef.current = 0;
    guideSlotSyncRef.current = null;
    telemetryEmitAtRef.current = 0;
    setGuideSlot(null);
    setHint(null);
    setAnswerFeedback(null);
    setLiveTelemetry({
      signalLabel: null,
      signalDesc: null,
      targetSlot: null,
      holdRemainSec: null,
      guideVisible: false,
      cooldownRemainSec: null,
    });
    if (feedbackClearRef.current) {
      clearTimeout(feedbackClearRef.current);
      feedbackClearRef.current = null;
    }
  }, [idx, task?.id]);

  const finishMultiAndNext = () => {
    if (!task || !isMulti) return;
    const sel = answersRef.current[task.id];
    if (Array.isArray(sel) && sel.length > 0) {
      triggerAnswerFeedback(task, sel);
    }
    scheduleAutoNext({ ...answersRef.current }, idx);
  };

  useEffect(() => {
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    };
  }, [idx, task?.id]);

  useEffect(() => {
    if (!open || !movementTasks.length) return;
    setAllAnsweredBanner(movementTasks.every((t) => isTaskAnswered(t, answers)));
  }, [open, movementTasks, movementAnswerFingerprint]);

  if (!open) return null;

  if (!movementTasks.length) {
    return (
      <div className="fixed inset-0 z-[140] flex flex-col items-center justify-center bg-slate-900 p-6 text-center text-white">
        <p className="mb-4 text-lg font-bold">Không có câu trắc nghiệm / đúng sai để làm bằng vận động.</p>
        <button type="button" onClick={onClose} className="rounded-2xl bg-teal-500 px-6 py-3 font-black uppercase">
          Đóng
        </button>
      </div>
    );
  }

  const { correct: movScoreCorrect, graded: movScoreGraded, points10: movPoints10 } = movementScoreStats;
  const movScorePres = getMovementScorePresentation(movPoints10, movScoreGraded);

  return (
    <div className="fixed inset-0 z-[140] flex max-h-[100dvh] min-h-0 flex-col overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-teal-950 text-white">
      <MovementAnswerFeedback variant={answerFeedback} />
      <audio ref={audioRef} src={MOVEMENT_BG_MUSIC_URL} preload="metadata" className="hidden" />
      <audio ref={questionAudioRef} preload="metadata" className="hidden" />

      <header className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2 sm:px-4 sm:py-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-teal-300">Làm bài kết hợp vận động</p>
          <p className="truncate text-xs font-bold text-white/90 sm:text-sm">
            Câu {idx + 1}/{movementTasks.length} · Đã làm {answeredCount}/{movementTasks.length}
            {isMulti && ' · Nhiều đáp án'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className="flex rounded-xl border border-white/15 bg-black/20 p-0.5"
            role="group"
            aria-label="Chọn camera"
          >
            <button
              type="button"
              onClick={() => setCameraFacing('user')}
              className={`rounded-[10px] px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wide sm:text-[10px] ${
                cameraFacing === 'user' ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
              }`}
            >
              Trước
            </button>
            <button
              type="button"
              onClick={() => setCameraFacing('environment')}
              className={`rounded-[10px] px-2.5 py-1.5 text-[9px] font-black uppercase tracking-wide sm:text-[10px] ${
                cameraFacing === 'environment' ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
              }`}
            >
              Sau
            </button>
          </div>
          <div
            className="flex flex-wrap rounded-xl border border-white/15 bg-black/20 p-0.5"
            role="group"
            aria-label="Chế độ theo dõi cử chỉ"
          >
            {(['head', 'pose', 'hand'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setTrackMode(m)}
                className={`rounded-[10px] px-2 py-1.5 text-[8px] font-black uppercase tracking-wide sm:px-2.5 sm:text-[9px] ${
                  trackMode === m ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
                }`}
              >
                {m === 'head' ? 'Nghiêng đầu' : m === 'pose' ? 'Dáng người' : 'Cử chỉ tay'}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCameraSetupOpen(true)}
            className="rounded-xl border border-amber-400/35 bg-amber-500/15 px-2 py-1.5 text-[9px] font-black uppercase tracking-wide text-amber-100 hover:bg-amber-500/25 sm:px-3 sm:text-[10px]"
            title="Kiểm tra camera và nhận diện cử chỉ"
          >
            Thiết lập camera
          </button>
          <button
            type="button"
            onClick={() => setShowCameraPreview((v) => !v)}
            className={`rounded-xl border px-2 py-1.5 text-[9px] font-black uppercase tracking-wide sm:px-3 sm:text-[10px] ${
              showCameraPreview
                ? 'border-white/20 bg-white/10 text-white/90 hover:bg-white/15'
                : 'border-teal-400/40 bg-teal-600/30 text-teal-50 hover:bg-teal-600/45'
            }`}
            title={
              showCameraPreview
                ? 'Ẩn khung camera trên màn hình — nhận diện vẫn chạy ở chế độ nền'
                : 'Hiện lại khung camera'
            }
          >
            {showCameraPreview ? (
              <EyeSlashIcon className="inline h-4 w-4 sm:mr-1" />
            ) : (
              <EyeIcon className="inline h-4 w-4 sm:mr-1" />
            )}
            <span className="hidden sm:inline">{showCameraPreview ? 'Ẩn camera' : 'Hiện camera'}</span>
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="rounded-xl border border-white/20 bg-white/5 p-1.5 text-white/85 hover:bg-white/15 sm:p-2"
            title="Cài đặt hiển thị"
            aria-label="Cài đặt hiển thị"
          >
            <Cog6ToothIcon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          </button>
          <label className="flex items-center gap-2 text-[10px] font-bold text-white/70">
            <span className="hidden sm:inline">Nhạc nền</span>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round(audioVol * 100)}
              onChange={(e) => setAudioVol(Number(e.target.value) / 100)}
              className="h-1 w-20 accent-teal-400 sm:w-28"
            />
            <span className="w-8 tabular-nums">{audioVol < 0.02 ? 'Tắt' : `${Math.round(audioVol * 100)}%`}</span>
          </label>
          <button
            type="button"
            onClick={onClose}
            className="flex shrink-0 items-center gap-2 rounded-2xl bg-white/10 px-3 py-2 text-[10px] font-black uppercase tracking-wider hover:bg-white/20 sm:px-4 sm:text-xs"
          >
            <XMarkIcon className="h-4 w-4" /> Đóng
          </button>
        </div>
      </header>

      {allAnsweredBanner && (
        <div className="shrink-0 border-b border-emerald-500/40 bg-emerald-600/25 px-4 py-2.5 text-center">
          <p className="text-sm font-bold text-emerald-100">
            Đã trả lời hết các câu trắc nghiệm trong chế độ này. Nhận diện đã tắt — xem điểm trong bảng tổng kết.
          </p>
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {!sessionStarted && !isSubmitted && (
          <div
            className="absolute inset-0 z-[45] flex flex-col items-center justify-center gap-4 bg-slate-950/72 px-4 backdrop-blur-[3px]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="movement-start-title"
          >
            <p id="movement-start-title" className="max-w-sm text-center text-sm font-bold text-white/95 sm:text-base">
              Nhấn «Bắt đầu» để đếm ngược và bật nhận diện cử chỉ. Bạn vẫn có thể dùng «Thiết lập camera» trên thanh công cụ.
            </p>
            <button
              type="button"
              onClick={() => setSessionStarted(true)}
              className="flex items-center gap-2 rounded-2xl bg-teal-500 px-8 py-3.5 text-sm font-black uppercase tracking-widest text-white shadow-lg shadow-teal-900/40 transition hover:bg-teal-400"
            >
              <PlayIcon className="h-5 w-5" aria-hidden />
              Bắt đầu
            </button>
          </div>
        )}
        {/* Nửa trên: câu hỏi — chia đôi chiều cao với vùng đáp án + camera (flex-1 / flex-1) */}
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden border-b border-teal-500/30 px-2 py-1 sm:px-3 sm:py-1.5">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          {task && (
            <>
              <div className="rounded-2xl border-2 border-teal-500/45 bg-slate-900/40 p-2 shadow-[inset_0_0_0_1px_rgba(45,212,191,0.12)] sm:p-3">
                <div
                  className={`flex min-h-0 flex-1 gap-2 sm:gap-3 ${task.imageUrl ? 'flex-col sm:flex-row sm:items-stretch' : 'flex-col'}`}
                >
                  {task.imageUrl ? (
                    <div className="flex w-full shrink-0 flex-col sm:w-[min(45%,320px)]">
                      <div className="overflow-hidden rounded-xl border-2 border-dashed border-amber-400/55 bg-amber-500/10 p-1.5 sm:p-2 shadow-inner ring-1 ring-amber-400/20">
                        <img
                          src={formatAssetUrl(task.imageUrl) || task.imageUrl}
                          alt=""
                          className="mx-auto max-h-[min(22vh,220px)] w-full object-contain sm:max-h-[min(28vh,280px)] transition-transform duration-500 hover:scale-[1.03]"
                        />
                      </div>
                      <p className="mt-1 text-center text-[8px] font-black uppercase tracking-wider text-amber-200/80">
                        Hình ảnh minh họa
                      </p>
                    </div>
                  ) : null}
                  <div
                    className={`flex min-w-0 flex-1 items-center rounded-xl border-2 border-dashed border-amber-400/50 bg-amber-500/[0.08] p-3 sm:p-4 ${
                      !task.imageUrl ? 'justify-center text-center' : ''
                    }`}
                  >
                    <div style={{ fontSize: `${questionFontPx}px` }} className={!task.imageUrl ? 'max-w-2xl' : ''}>
                      <MathMixedText
                        text={String(task.description || '')}
                        className="font-bold leading-snug text-white"
                      />
                    </div>
                  </div>
                </div>

                {task.audioUrl ? (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
                    <span className="text-[10px] font-black uppercase tracking-wide text-teal-200/95">Âm thanh câu hỏi</span>
                    <button
                      type="button"
                      onClick={toggleQuestionAudio}
                      className="inline-flex items-center gap-1.5 rounded-xl bg-teal-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-white hover:bg-teal-500"
                    >
                      {questionAudioPlaying ? (
                        <>
                          <PauseIcon className="h-4 w-4" /> Tạm dừng
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4" /> Phát
                        </>
                      )}
                    </button>
                    {!questionSpeechDone && (
                      <span className="text-[9px] font-medium text-amber-200/90">Nhạc nền chờ sau khi hết âm thanh</span>
                    )}
                  </div>
                ) : null}

                {isTF && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-6 border-t border-white/10 pt-3 text-[10px] font-black uppercase tracking-wide text-white/95 sm:gap-14">
                    <span>
                      Đúng <span className="text-teal-300">· TRÁI</span>
                    </span>
                    <span className="hidden text-white/25 sm:inline">|</span>
                    <span>
                      Sai <span className="text-teal-300">· PHẢI</span>
                    </span>
                  </div>
                )}
              </div>

              {Array.isArray(task.content?.optionImages) && task.content.optionImages.some(Boolean) && (
                <div className="flex flex-wrap gap-2">
                  {task.content.optionImages.map((url: string | undefined, ii: number) =>
                    url ? (
                      <div
                        key={ii}
                        className="overflow-hidden rounded-lg border border-dashed border-amber-400/35 bg-amber-500/10"
                      >
                        <img
                          src={formatAssetUrl(url) || url}
                          alt=""
                          className="h-16 w-auto max-w-[100px] object-cover sm:h-20"
                        />
                      </div>
                    ) : null
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </section>

        {/* Nửa dưới: camera + đáp án — 50/50 với vùng câu hỏi */}
        <section className="flex min-h-0 flex-1 flex-row items-stretch gap-1.5 overflow-hidden px-2 pb-1.5 pt-0.5 sm:gap-2 sm:px-2 sm:pb-2">
          <div
            className={
              showCameraPreview
                ? 'relative flex h-full min-h-0 w-[min(42%,280px)] shrink-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/60 sm:max-w-[300px]'
                : 'relative h-0 w-0 shrink-0 overflow-visible'
            }
            aria-hidden={!showCameraPreview}
          >
            <div
              className={
                showCameraPreview
                  ? 'relative flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                  : 'pointer-events-none fixed left-[-9999px] top-0 z-0 flex h-[min(480px,55vh)] w-[min(640px,90vw)] flex-col overflow-hidden opacity-0'
              }
            >
            <video
              ref={videoRef}
              className="h-full min-h-[96px] w-full min-w-[120px] flex-1 object-cover object-center sm:min-h-[120px]"
              playsInline
              muted
            />
            {countdownTick !== null && showCameraPreview && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-black/55 backdrop-blur-[2px]">
                <span
                  className="px-2 text-center font-black tabular-nums tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.85)] animate-in zoom-in-95 duration-200"
                  style={{
                    fontSize: countdownTick === 'go' ? 'clamp(1.25rem, 5vw, 2rem)' : 'clamp(2.5rem, 12vw, 5rem)',
                  }}
                >
                  {countdownTick === 'go' ? 'Bắt đầu!' : countdownTick}
                </span>
                {countdownTick !== 'go' && typeof countdownTick === 'number' && (
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/75">Chuẩn bị</span>
                )}
              </div>
            )}
            {!camError && task && detectionRunning && !cameraSetupOpen && (
              <CameraPoseGuides
                mode={trackMode}
                optionCount={optionCount}
                isTrueFalse={!!isTF}
                activeSlot={guideSlot}
                isMulti={!!isMulti}
                splitLabels={splitLabelsForCamera(!!isTF, optionCount, options, trackMode)}
                holdConfirmSeconds={holdConfirmMs / 1000}
                showAbcdLabels={showMcqLetters}
              />
            )}
            {camError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/75 p-2 text-center text-[11px] leading-snug text-amber-200">
                {camError}
              </div>
            )}
            {engineError && (
              <div className="absolute bottom-1 left-1 right-1 rounded-lg bg-rose-900/95 p-1.5 text-center text-[10px] leading-tight text-rose-100">
                {engineError}
              </div>
            )}
            </div>
          </div>

          <div
            className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-y-auto py-0.5"
            style={{ fontSize: `${uiFontPx}px`, color: uiTextColor }}
          >
            {task && (
              <>
                <div
                  className={`grid gap-2 ${
                    isMulti
                      ? 'grid-cols-1'
                      : optionCount <= 2
                        ? 'grid-cols-2'
                        : optionCount === 3
                          ? 'grid-cols-3'
                          : 'grid-cols-2'
                  }`}
                >
                  {options.map((opt, i) => {
                    const sel =
                      isMulti && Array.isArray(answers[task.id])
                        ? answers[task.id].includes(opt)
                        : answers[task.id] === opt;
                    const poseHint = getPoseHintForSlot(i, trackMode, !!isTF, optionCount);
                    const graded = sel && !isMulti && movementTaskHasDefinedCorrectAnswer(task);
                    const answerCorrect = graded && isMcqTfStudentAnswerCorrect(task, answers[task.id]);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => {
                          if (isMulti) {
                            const cur = Array.isArray(answers[task.id]) ? answers[task.id] : [];
                            const next = cur.includes(opt) ? cur.filter((x: string) => x !== opt) : [...cur, opt];
                            onAnswerChange(task.id, next);
                          } else {
                            onAnswerChange(task.id, opt);
                            triggerAnswerFeedback(task, opt);
                            const merged = { ...answersRef.current, [task.id]: opt };
                            scheduleAutoNext(merged, idx);
                          }
                        }}
                        className={`relative flex min-h-[4.5rem] flex-col items-stretch gap-1.5 rounded-xl border-2 border-dashed px-2.5 py-2.5 text-left transition-all sm:min-h-[5rem] sm:px-3 ${
                          graded && answerCorrect
                            ? 'border-emerald-400 bg-emerald-950/35 shadow-[0_0_14px_rgba(52,211,153,0.25)] ring-1 ring-emerald-500/30'
                            : graded && !answerCorrect
                              ? 'border-red-500 bg-red-950/35 shadow-[0_0_14px_rgba(248,113,113,0.2)] ring-1 ring-red-500/30'
                              : sel
                                ? 'border-teal-400 bg-teal-600/30 shadow-[0_0_16px_rgba(45,212,191,0.25)]'
                                : 'border-orange-400/50 bg-orange-500/10 hover:border-orange-300/60 hover:bg-orange-500/18'
                        }`}
                      >
                        {graded && (
                          <span
                            className={`absolute right-2 top-1.5 text-xl font-black leading-none ${
                              answerCorrect ? 'text-emerald-300' : 'text-red-400'
                            }`}
                            aria-hidden
                          >
                            {answerCorrect ? '✓' : '✗'}
                          </span>
                        )}
                        <div className="flex items-start gap-2 pr-6">
                          <OptionPoseInline
                            slotIndex={i}
                            mode={trackMode}
                            isTrueFalse={!!isTF}
                            optionCount={optionCount}
                            poseHint={poseHint}
                          />
                          <div className="min-w-0 flex-1" style={{ color: 'inherit' }}>
                            {showMcqLetters ? (
                              <span className="font-black opacity-95" style={{ color: 'inherit' }}>
                                {getMcqOptionLabel(i)}
                              </span>
                            ) : null}
                            <MathMixedText
                              text={String(opt)}
                              className={`block font-bold leading-snug text-inherit opacity-95 ${showMcqLetters ? 'mt-1' : ''}`}
                            />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

              {isMulti && (
                <div className="mt-1 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={finishMultiAndNext}
                    className="w-full rounded-2xl bg-teal-600 py-2.5 text-[10px] font-black uppercase tracking-wider text-white hover:bg-teal-500 sm:text-xs"
                  >
                    Xong câu này →
                  </button>
                </div>
              )}

              <div className="flex gap-2 border-t border-white/10 pt-3">
                <button
                  type="button"
                  disabled={idx <= 0}
                  onClick={() => setIdx((i) => Math.max(0, i - 1))}
                  className="flex-1 rounded-2xl bg-white/10 py-2.5 text-[10px] font-black uppercase disabled:opacity-40 sm:text-xs"
                >
                  ← Trước
                </button>
                <button
                  type="button"
                  disabled={idx >= movementTasks.length - 1}
                  onClick={() => setIdx((i) => Math.min(movementTasks.length - 1, i + 1))}
                  className="flex-1 rounded-2xl bg-white/10 py-2.5 text-[10px] font-black uppercase disabled:opacity-40 sm:text-xs"
                >
                  Sau →
                </button>
              </div>

              <div
                className="mt-1 shrink-0 border-t border-white/10 pt-2 leading-relaxed"
                style={{ color: uiTextColor, fontSize: `${Math.max(10, uiFontPx - 1)}px`, opacity: 0.92 }}
              >
                {!showCameraDataPanel ? (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-bold text-white/75">Dữ liệu camera</span>
                    <button
                      type="button"
                      onClick={() => setShowCameraDataPanel(true)}
                      className="rounded-lg border border-white/20 bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white/90 hover:bg-white/20"
                    >
                      Bật hiển thị
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <p className="font-bold text-white/75">Dữ liệu camera — phiên chơi</p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setShowCameraDataPanel(false)}
                          className="rounded-lg border border-white/15 bg-white/5 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-white/75 hover:bg-white/10 sm:text-[10px]"
                        >
                          Ẩn khối
                        </button>
                        <button
                          type="button"
                          onClick={() => setCameraDataDetailOpen((v) => !v)}
                          className="rounded-lg border border-teal-500/40 bg-teal-600/25 px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-teal-100 hover:bg-teal-600/40 sm:text-[10px]"
                        >
                          {cameraDataDetailOpen ? 'Thu gọn' : 'Chi tiết'}
                        </button>
                      </div>
                    </div>
                    {hint ? (
                      <p
                        className="mb-2 rounded-lg bg-teal-600/30 px-2.5 py-1.5 font-medium leading-snug text-teal-50"
                        style={{ fontSize: `${Math.max(10, uiFontPx)}px` }}
                      >
                        {hint}
                      </p>
                    ) : (
                      <p className="mb-2 text-[10px] text-white/50">Chưa có gợi ý từ camera — chờ khung hình hoặc bật nhận diện.</p>
                    )}
                    <ul className="list-none space-y-0.5 font-mono text-[10px] text-white/85 sm:text-[11px]">
                      <li>
                        <span className="text-white/55">Nhận diện:</span>{' '}
                        {allAnswered
                          ? 'đã tắt (đã trả lời hết)'
                          : detectionRunning
                            ? 'đang chạy'
                            : 'chưa (chờ sau đếm hoặc bấm Bắt đầu)'}
                      </li>
                      <li>
                        <span className="text-white/55">Chế độ theo dõi:</span>{' '}
                        {trackMode === 'head' ? 'Nghiêng đầu' : trackMode === 'pose' ? 'Dáng người' : 'Cử chỉ tay'}
                      </li>
                      <li>
                        <span className="text-white/55">Tín hiệu:</span> {liveTelemetry.signalDesc ?? '—'}
                      </li>
                      <li>
                        <span className="text-white/55">Còn giữ để chốt:</span>{' '}
                        {liveTelemetry.holdRemainSec !== null ? `${liveTelemetry.holdRemainSec.toFixed(1)}s` : '—'}
                      </li>
                    </ul>
                    {cameraDataDetailOpen ? (
                      <ul className="mt-2 list-none space-y-0.5 border-t border-white/10 pt-2 font-mono text-[10px] text-white/85 sm:text-[11px]">
                        <li>
                          <span className="text-white/55">Câu:</span> {idx + 1}/{movementTasks.length} ·{' '}
                          <span className="text-white/55">Đã làm:</span> {answeredCount}/{movementTasks.length}
                        </li>
                        <li>
                          <span className="text-white/55">Loại câu:</span>{' '}
                          {isTF ? 'Đúng / Sai' : isMulti ? 'Nhiều đáp án' : 'Một đáp án'}
                        </li>
                        <li>
                          <span className="text-white/55">Số phương án:</span> {optionCount}
                          {showMcqLetters ? ' · nhãn A–D: bật' : ' · nhãn A–D: tắt (chỉ khi đủ 4 phương án)'}
                        </li>
                        <li>
                          <span className="text-white/55">Camera:</span> {cameraFacing === 'user' ? 'Trước (selfie)' : 'Sau'}{' '}
                          · <span className="text-white/55">gương trục ngang:</span> {cameraFacing === 'user' ? 'có' : 'không'}
                        </li>
                        {trackMode === 'head' ? (
                          <li>
                            <span className="text-white/55">Độ nhạy nghiêng đầu:</span> {headSensitivity.toFixed(2)}
                          </li>
                        ) : null}
                        <li>
                          <span className="text-white/55">Giữ để chốt / nghỉ sau chốt:</span> {(holdConfirmMs / 1000).toFixed(2)}s /{' '}
                          {(COOLDOWN_MS / 1000).toFixed(2)}s
                        </li>
                        <li>
                          <span className="text-white/55">Đếm ngược:</span>{' '}
                          {countdownTick === null
                            ? '—'
                            : countdownTick === 'go'
                              ? 'Bắt đầu!'
                              : countdownTick}
                        </li>
                        <li>
                          <span className="text-white/55">Mã tín hiệu:</span> {liveTelemetry.signalLabel ?? '—'}
                        </li>
                        <li>
                          <span className="text-white/55">Ô đang nhắm:</span>{' '}
                          {liveTelemetry.targetSlot !== null
                            ? showMcqLetters
                              ? getMcqOptionLabel(liveTelemetry.targetSlot)
                              : `Ô ${liveTelemetry.targetSlot + 1}`
                            : '—'}
                        </li>
                        <li>
                          <span className="text-white/55">Nghỉ sau chốt (cooldown):</span>{' '}
                          {liveTelemetry.cooldownRemainSec !== null ? `${liveTelemetry.cooldownRemainSec.toFixed(2)}s` : '—'}
                        </li>
                        <li>
                          <span className="text-white/55">Gợi ý ô sáng:</span> {liveTelemetry.guideVisible ? 'có' : 'chưa'}
                        </li>
                      </ul>
                    ) : null}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {countdownTick !== null && !showCameraPreview && (
        <div className="pointer-events-none absolute inset-0 z-[43] flex items-center justify-center bg-black/50 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-white/20 bg-slate-950/92 px-10 py-8 shadow-2xl">
            <span
              className="text-center font-black tabular-nums tracking-tight text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.85)]"
              style={{
                fontSize: countdownTick === 'go' ? 'clamp(1.5rem, 6vw, 2.25rem)' : 'clamp(2.75rem, 14vw, 5.5rem)',
              }}
            >
              {countdownTick === 'go' ? 'Bắt đầu!' : countdownTick}
            </span>
            {countdownTick !== 'go' && typeof countdownTick === 'number' && (
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">Chuẩn bị</span>
            )}
          </div>
        </div>
      )}
      </div>

      {cameraSetupOpen && (
        <div
          className="fixed inset-0 z-[155] flex flex-col bg-slate-950/97 backdrop-blur-[2px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="movement-camera-setup-title"
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-3 sm:px-4">
            <div className="min-w-0">
              <p id="movement-camera-setup-title" className="text-[10px] font-black uppercase tracking-[0.2em] text-teal-300">
                Thiết lập camera
              </p>
              <p className="mt-0.5 text-xs text-white/85 sm:text-sm">
                Kiểm tra khung hình và nhận diện cử chỉ trước khi làm bài
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setCameraSetupOpen(false)}
                className="rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-[10px] font-black uppercase tracking-wide text-white hover:bg-white/20 sm:text-xs"
              >
                Hoàn tất thiết lập
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3 sm:flex-row sm:gap-6 sm:p-4">
            <div className="relative flex min-h-[200px] flex-[1.2] overflow-hidden rounded-2xl border border-white/15 bg-black shadow-lg sm:min-h-[min(52vh,440px)]">
              <video
                ref={videoSetupRef}
                className="h-full min-h-[180px] w-full flex-1 object-cover object-center"
                playsInline
                muted
              />
              {!camError && task && detectionRunningForLoopUi && (
                <CameraPoseGuides
                  mode={trackMode}
                  optionCount={optionCount}
                  isTrueFalse={!!isTF}
                  activeSlot={guideSlot}
                  isMulti={!!isMulti}
                  splitLabels={splitLabelsForCamera(!!isTF, optionCount, options, trackMode)}
                  holdConfirmSeconds={holdConfirmMs / 1000}
                  showAbcdLabels={showMcqLetters}
                />
              )}
              {camError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4 text-center text-sm leading-snug text-amber-200">
                  {camError}
                </div>
              )}
              {engineError && (
                <div className="absolute bottom-2 left-2 right-2 rounded-lg bg-rose-900/95 p-2 text-center text-[10px] text-rose-100">
                  {engineError}
                </div>
              )}
            </div>

            <div className="flex w-full shrink-0 flex-col gap-4 sm:max-w-sm">
              <p className="text-[11px] leading-relaxed text-white/80">
                Dùng các nút dưới đây giống lúc làm bài: đổi camera Trước/Sau nếu cần, chọn chế độ theo dõi và quan sát khung gợi ý trên hình. Khi thấy tín hiệu và gợi ý phản hồi đúng ý, bấm «Hoàn tất thiết lập».
              </p>

              <div className="flex flex-wrap gap-2">
                <div className="flex rounded-xl border border-white/15 bg-black/30 p-0.5" role="group" aria-label="Chọn camera">
                  <button
                    type="button"
                    onClick={() => setCameraFacing('user')}
                    className={`rounded-[10px] px-2.5 py-1.5 text-[9px] font-black uppercase sm:text-[10px] ${
                      cameraFacing === 'user' ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Trước
                  </button>
                  <button
                    type="button"
                    onClick={() => setCameraFacing('environment')}
                    className={`rounded-[10px] px-2.5 py-1.5 text-[9px] font-black uppercase sm:text-[10px] ${
                      cameraFacing === 'environment' ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
                    }`}
                  >
                    Sau
                  </button>
                </div>
                <div
                  className="flex flex-wrap rounded-xl border border-white/15 bg-black/30 p-0.5"
                  role="group"
                  aria-label="Chế độ theo dõi"
                >
                  {(['head', 'pose', 'hand'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setTrackMode(m)}
                      className={`rounded-[10px] px-2 py-1.5 text-[8px] font-black uppercase sm:px-2.5 sm:text-[9px] ${
                        trackMode === m ? 'bg-teal-500 text-white shadow-sm' : 'text-white/70 hover:text-white'
                      }`}
                    >
                      {m === 'head' ? 'Nghiêng đầu' : m === 'pose' ? 'Dáng người' : 'Cử chỉ tay'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-white/10 bg-black/25 p-3">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-teal-200/90">Thử nhận diện</p>
                {hint ? (
                  <p className="mb-2 rounded-lg bg-teal-600/25 px-2 py-1.5 text-[11px] font-medium leading-snug text-teal-50">{hint}</p>
                ) : (
                  <p className="mb-2 text-[11px] text-white/55">Chưa thấy tín hiệu — điều chỉnh ánh sáng hoặc đổi chế độ.</p>
                )}
                <ul className="space-y-1 font-mono text-[10px] text-white/80">
                  <li>
                    <span className="text-white/50">Giữ để chốt:</span> {(holdConfirmMs / 1000).toFixed(2)}s
                  </li>
                  <li>
                    <span className="text-white/50">Tín hiệu:</span> {liveTelemetry.signalDesc ?? '—'}
                  </li>
                  <li>
                    <span className="text-white/50">Ô gợi ý:</span>{' '}
                    {liveTelemetry.targetSlot !== null
                      ? showMcqLetters
                        ? getMcqOptionLabel(liveTelemetry.targetSlot)
                        : `Ô ${liveTelemetry.targetSlot + 1}`
                      : '—'}
                  </li>
                  <li>
                    <span className="text-white/50">Còn giữ:</span>{' '}
                    {liveTelemetry.holdRemainSec !== null ? `${liveTelemetry.holdRemainSec.toFixed(1)}s` : '—'}
                  </li>
                </ul>
              </div>

              <p className="text-[10px] leading-snug text-white/45">
                Chỉnh độ nhạy nghiêng đầu, thời gian giữ để chốt, cỡ chữ… trong «Cài đặt hiển thị» (nút bánh răng trên thanh công cụ chính).
              </p>
            </div>
          </div>
        </div>
      )}

      {allAnswered && !movementScoreDismissed && !isSubmitted && (
        <div
          className="fixed inset-0 z-[148] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="movement-score-title"
        >
          <div
            className={`relative max-w-md overflow-hidden rounded-3xl border-2 p-6 text-center shadow-2xl sm:p-8 ${movScorePres.panel}`}
          >
            <div
              className={`pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-gradient-to-br opacity-40 blur-3xl ${
                movPoints10 != null && movPoints10 >= 8
                  ? 'from-amber-300 to-teal-400'
                  : movPoints10 != null && movPoints10 >= 5
                    ? 'from-teal-400 to-slate-600'
                    : 'from-rose-400 to-slate-700'
              }`}
            />
            <p id="movement-score-title" className={`relative text-lg font-black uppercase tracking-wide sm:text-xl ${movScorePres.accent}`}>
              {movScorePres.title}
            </p>
            {movScoreGraded > 0 && movPoints10 != null ? (
              <p className="relative mt-4 font-black tabular-nums text-white" style={{ fontSize: 'clamp(2.5rem, 12vw, 4rem)' }}>
                {movPoints10.toFixed(1)}
                <span className="text-xl font-bold text-white/70 sm:text-2xl">/10</span>
              </p>
            ) : null}
            <p className="relative mt-3 text-sm leading-relaxed text-white/90">{movScorePres.subtitle}</p>
            {movScoreGraded > 0 ? (
              <p className="relative mt-2 text-xs text-white/65">
                Đúng {movScoreCorrect}/{movScoreGraded} câu có đáp án chấm tự động trong phần vận động.
              </p>
            ) : (
              <p className="relative mt-2 text-xs text-white/65">Giáo viên chưa gán đáp án đúng cho một số câu — chỉ ghi nhận hoàn thành.</p>
            )}
            <button
              type="button"
              onClick={() => setMovementScoreDismissed(true)}
              className="relative mt-6 w-full rounded-2xl bg-teal-500 py-3 text-sm font-black uppercase tracking-wider text-white shadow-lg hover:bg-teal-400"
            >
              Đã hiểu
            </button>
          </div>
        </div>
      )}

      {settingsOpen && (
        <div
          className="fixed inset-0 z-[160] flex items-center justify-center bg-black/65 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="movement-settings-title"
          onClick={() => setSettingsOpen(false)}
        >
          <div
            className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/15 bg-slate-900 p-4 text-[11px] text-white/90 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-2 border-b border-white/10 pb-2">
              <h2 id="movement-settings-title" className="text-xs font-black uppercase tracking-wide text-teal-200">
                Cài đặt hiển thị
              </h2>
              <button
                type="button"
                onClick={() => setSettingsOpen(false)}
                className="rounded-lg p-1 text-white/70 hover:bg-white/10"
                aria-label="Đóng cài đặt"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {homeworkId ? (
              <p className="mb-2 text-[10px] leading-snug text-teal-300/90">
                Cài đặt được lưu theo bài tập này — lần sau mở chế độ vận động sẽ giữ nguyên.
              </p>
            ) : (
              <p className="mb-2 text-[10px] leading-snug text-white/55">
                Cài đặt lưu trên trình duyệt (không gắn theo từng bài).
              </p>
            )}
            <div className="space-y-3 pt-1">
              {trackMode === 'head' && (
                <label className="flex flex-col gap-1">
                  <span className="text-white/70">Độ nhạy nghiêng đầu (càng lớn càng dễ nhận)</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={0.35}
                      max={2.75}
                      step={0.05}
                      value={headSensitivity}
                      onChange={(e) => setHeadSensitivity(Number(e.target.value))}
                      className="h-1.5 flex-1 accent-teal-400"
                    />
                    <span className="w-10 tabular-nums text-white/90">{headSensitivity.toFixed(2)}</span>
                  </div>
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-white/70">Cỡ chữ phần câu hỏi (px)</span>
                <input
                  type="number"
                  min={12}
                  max={22}
                  value={questionFontPx}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (Number.isFinite(n)) setQuestionFontPx(Math.min(22, Math.max(12, n)));
                  }}
                  className="w-full rounded-lg border border-white/15 bg-black/30 px-2 py-1.5 text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-white/70">Cỡ chữ vùng đáp án và gợi ý (px)</span>
                <input
                  type="number"
                  min={11}
                  max={22}
                  value={uiFontPx}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    if (Number.isFinite(n)) setUiFontPx(Math.min(22, Math.max(11, n)));
                  }}
                  className="w-full rounded-lg border border-white/15 bg-black/30 px-2 py-1.5 text-white"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-white/70">Màu chữ vùng đáp án và gợi ý</span>
                <input
                  type="color"
                  value={uiTextColor}
                  onChange={(e) => setUiTextColor(e.target.value)}
                  className="h-9 w-full cursor-pointer rounded-lg border border-white/15 bg-black/30"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-white/70">Thời gian giữ tư thế để xác nhận đáp án</span>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={HOLD_MIN_MS}
                    max={HOLD_MAX_MS}
                    step={250}
                    value={holdConfirmMs}
                    onChange={(e) =>
                      setHoldConfirmMs(Math.min(HOLD_MAX_MS, Math.max(HOLD_MIN_MS, Number(e.target.value))))
                    }
                    className="h-1.5 flex-1 accent-teal-400"
                  />
                  <span className="w-14 tabular-nums text-white/90">{(holdConfirmMs / 1000).toFixed(2)}s</span>
                </div>
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={showCameraDataPanel}
                  onChange={(e) => setShowCameraDataPanel(e.target.checked)}
                  className="h-4 w-4 rounded border-white/30 accent-teal-500"
                />
                <span className="text-white/70">Hiển thị khối dữ liệu camera khi làm bài</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={showCameraPreview}
                  onChange={(e) => setShowCameraPreview(e.target.checked)}
                  className="h-4 w-4 rounded border-white/30 accent-teal-500"
                />
                <span className="text-white/70">Hiển thị khung hình camera (tắt vẫn nhận diện ở chế độ nền)</span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
